import { useEffect, useMemo, useState, type Key } from 'react';
import { useMany, useTranslate } from '@refinedev/core';
import { DeleteButton } from '@refinedev/antd';
import { Button, Card, Space, Table, Tooltip, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type {
  OrganizationOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import {
  buildOrganizationPublisherLinkTree,
  type OrganizationPublisherLinkRow,
} from '@/components/tenancy/organizationPublisherLinks';
import { OrganizationPublisherStatusTag } from '@/components/tenancy/tags';
import { PUBLISHER_TREE_LIMIT } from '@/components/user_catalog/publishers/publisherTree';
import { useOrganizationPublisherLinks } from '@/hooks/tenancy/useOrganizationPublisherLinks';
import { formatDate, formatDateTime } from '@/utils/format';
import { AddPublisherModal } from './AddPublisherModal';

const { Text } = Typography;

interface OrganizationPublishersTabProps {
  record: OrganizationOut;
}

/** Ids of the rows that have something to expand. */
const branchIds = (rows: OrganizationPublisherLinkRow[]): number[] =>
  rows.flatMap((row) =>
    row.children ? [row.id, ...branchIds(row.children)] : []
  );

export const OrganizationPublishersTab = ({
  record,
}: OrganizationPublishersTabProps) => {
  const t = useTranslate();
  // `parentLink` survives closing, so the title doesn't flip mid-animation.
  const [modalState, setModalState] = useState<{
    open: boolean;
    parentLink: OrganizationPublisherLinkRow | null;
  }>({ open: false, parentLink: null });
  const { links, isLoading } = useOrganizationPublisherLinks(record.id);

  const publisherIds = [...new Set(links.map((link) => link.publisher_id))];
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    meta: { params: { limit: PUBLISHER_TREE_LIMIT } },
    queryOptions: { enabled: publisherIds.length > 0 },
  });
  const publishers = publishersResult?.data;
  const publisherMap = new Map(
    (publishers ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  const rows = useMemo(
    () => buildOrganizationPublisherLinkTree(links, publishers ?? []),
    [links, publishers]
  );

  // `defaultExpandAllRows` is read on the first render only, when the rows are
  // still loading, so the branches have to be opened once the data lands.
  const [expandedKeys, setExpandedKeys] = useState<readonly Key[]>([]);
  useEffect(() => setExpandedKeys(branchIds(rows)), [rows]);

  const nextOrganizationIds = [
    ...new Set(
      links
        .map((link) => link.next?.organization_id)
        .filter((id): id is number => id != null)
    ),
  ];
  const { result: organizationsResult } = useMany<OrganizationOut>({
    resource: 'tenancy.organizations',
    ids: nextOrganizationIds,
    queryOptions: { enabled: nextOrganizationIds.length > 0 },
  });
  const organizationMap = new Map(
    (organizationsResult?.data ?? []).map((o: OrganizationOut) => [o.id, o])
  );

  return (
    <>
      <Card
        // Counts every link, not just the roots the tree starts from.
        title={`${t('tenancy.organizations.sections.publishers')} (${links.length})`}
        extra={
          <Button
            icon={<PlusOutlined />}
            onClick={() => setModalState({ open: true, parentLink: null })}
          >
            {t('tenancy.organizations.actions.add_publisher')}
          </Button>
        }
      >
        <Table<OrganizationPublisherLinkRow>
          dataSource={rows}
          rowKey="id"
          size="small"
          loading={isLoading}
          // Pages would split a parent from its imprints.
          pagination={false}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: setExpandedKeys,
          }}
        >
          <Table.Column<OrganizationPublisherLinkRow>
            dataIndex="publisher_id"
            title={t('tenancy.organizations.fields.publisher')}
            render={(publisherId: number) => (
              <ShowIconLink resource="user_catalog.publishers" id={publisherId}>
                {publisherMap.get(publisherId)?.name_with_role ?? publisherId}
              </ShowIconLink>
            )}
          />
          <Table.Column<OrganizationPublisherLinkRow>
            dataIndex="valid_from"
            title={t('tenancy.organizations.fields.valid_from')}
            render={(value: string) => formatDate(value)}
          />
          <Table.Column<OrganizationPublisherLinkRow>
            dataIndex="status"
            title={t('tenancy.organizations.fields.link_status')}
            render={(status: string) => (
              <OrganizationPublisherStatusTag status={status} />
            )}
          />
          <Table.Column<OrganizationPublisherLinkRow>
            dataIndex="next"
            title={t('tenancy.organizations.fields.moved_to')}
            render={(next: OrganizationPublisherLinkRow['next']) =>
              orDash(
                next && (
                  <Space size="small" wrap>
                    <ShowIconLink
                      resource="tenancy.organizations"
                      id={next.organization_id}
                    >
                      {organizationMap.get(next.organization_id)?.name ??
                        next.organization_id}
                    </ShowIconLink>
                    <Text type="secondary">
                      {t('tenancy.organizations.publishers.moved_to_from', {
                        date: formatDate(next.valid_from),
                      })}
                    </Text>
                  </Space>
                )
              )
            }
          />
          <Table.Column<OrganizationPublisherLinkRow>
            dataIndex="created_at"
            title={t('base.fields.created_at')}
            render={(value: string) => formatDateTime(value)}
          />
          <Table.Column<OrganizationPublisherLinkRow>
            dataIndex="actions"
            title={t('actions.column')}
            render={(_, link) => {
              const publisher = publisherMap.get(link.publisher_id);
              return (
                <Space size="small">
                  {/* An ended parent is gone from here; if it came back, its
                      newer link carries the action. */}
                  {publisher &&
                    publisher.parent_id == null &&
                    link.status !== 'ended' && (
                      <Button
                        type="link"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() =>
                          setModalState({ open: true, parentLink: link })
                        }
                      >
                        {t('tenancy.organizations.actions.add_imprint')}
                      </Button>
                    )}
                  {/* A link that has started is history — the backend
                      refuses to withdraw it. A parent's link also stays while
                      imprints depend on it: the backend would let it go and
                      leave them without their parent here. */}
                  {link.status === 'scheduled' && (
                    <Tooltip
                      title={
                        link.children
                          ? t('tenancy.organizations.publishers.delete_blocked')
                          : undefined
                      }
                    >
                      {/* A disabled button swallows hover, so the tooltip
                          needs a wrapper of its own to stay reachable. */}
                      <span style={{ display: 'inline-block' }}>
                        <DeleteButton
                          hideText
                          size="small"
                          resource="tenancy.organization_publishers"
                          recordItemId={link.id}
                          disabled={link.children != null}
                        />
                      </span>
                    </Tooltip>
                  )}
                </Space>
              );
            }}
          />
        </Table>
      </Card>

      <AddPublisherModal
        open={modalState.open}
        onClose={() => setModalState((state) => ({ ...state, open: false }))}
        record={record}
        parentLink={modalState.parentLink}
        parentName={
          modalState.parentLink
            ? publisherMap.get(modalState.parentLink.publisher_id)?.name
            : undefined
        }
      />
    </>
  );
};
